// ChatWorkspaceSidebar — sidebar da nova superfície Chat (fatia A1).
//
// Duas seções, como o Pedro pediu:
//   • AGENTES (topo) — read-only em A1 (lista os agentes de /api/agents; o
//     relógio marca os agendados). Criar/agendar = fatia A5.
//   • SESSÕES (baixo) — os code_sessions scope='local' (= sessões de chat),
//     agrupáveis em PASTAS (reusa code_session_folders + store) com drag-drop.
//
// Reusa o store inteiro (sessions/folders/statuses/streaming) e as ações de
// pasta. Zero chrome de github. Tokens warm vêm do .cx-shell ancestral.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  CheckSquare,
  ChevronRight,
  Clock,
  Columns3,
  FolderPlus,
  Home,
  LayoutDashboard,
  MessageSquare,
  Play,
  PlugZap,
  Plus,
  Search,
  Square,
  Trash2,
  X,
} from 'lucide-react';
// Agente = ícone Huge Icons derivado do emoji gravado (dado intocado no banco).
import { HugeiconsIcon } from '@hugeicons/react';
import { iconForAgentEmoji } from '../../../lib/agentEmojiIcon.js';
import { apiFetch } from '../../../lib/api.js';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { evictRun } from '../../../lib/agentRunStore.js';
import { filterSessionsForSurface } from '../../../lib/code/sessionSidebarModel.js';
import { useScheduledTasks } from '../../../hooks/useScheduledTasks.js';
import { useLLMAdapters } from '../../../hooks/useLLMAdapters.js';
import { isTaskLocal, runLocalTaskNow } from '../../../lib/localTaskRun.js';
import MemoryEditor from '../../code/side-panel/MemoryEditor.jsx';
import SidebarNavItem from '../../code/SidebarNavItem.jsx';
import IntegrationsView from '../../IntegrationsView.jsx';
import CommandCenterModal from '../../code/CommandCenterModal.jsx';
import AgentModal from '../AgentModal.jsx';

const STATUS_COLOR = {
  running: 'var(--apolo-success)',
  blocked: 'var(--apolo-error)',
  review: 'var(--apolo-accent)',
  needs_you: 'var(--apolo-warning)',
  idle: 'var(--apolo-textTertiary)',
};

function Dot({ status }) {
  return (
    <span
      aria-hidden="true"
      className="h-1.5 w-1.5 shrink-0 rounded-full"
      style={{ background: STATUS_COLOR[status] || STATUS_COLOR.idle }}
    />
  );
}

// C3 (recuperação Aba-Apolo 67ecfdb0) — pill de schedule por agente: próximo
// run (pendente) ou último run (executado/falhou). GOTCHA da memória: o backend
// grava last_run_status='ok' nas runs boas — aceitar 'ok' OU 'success'.
function fmtDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${min}`;
}

function SchedulePill({ task }) {
  if (!task) return null;
  const failed = task.last_run_status === 'failed' || (task.fail_count > 0 && !task.last_run_status);
  const succeeded = task.last_run_status === 'ok' || task.last_run_status === 'success';
  const isPaused = task.status === 'paused';

  let color;
  let Icon;
  let label;
  if (failed) { color = 'text-apolo-error'; Icon = AlertCircle; label = 'Falhou'; }
  else if (isPaused) { color = 'text-apolo-warning'; Icon = Clock; label = 'Pausado'; }
  else if (succeeded) { color = 'text-apolo-success'; Icon = CheckCircle2; label = 'Executado'; }
  else { color = 'text-apolo-warning'; Icon = Clock; label = 'Pendente'; }

  const displayDate = (succeeded || failed) ? fmtDate(task.last_run_at) : fmtDate(task.next_run_at);
  const detail = failed && task.last_run_error ? ` — ${task.last_run_error}` : '';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${color} bg-apolo-surface3`}
      title={`${label}: ${displayDate || task.cron_expression || ''}${detail}`}
    >
      <Icon size={9} aria-hidden="true" />
      {displayDate || task.cron_expression}
    </span>
  );
}

export default function ChatWorkspaceSidebar({ onNewSession }) {
  const sessions = useAgentStore((s) => s.code.sessions);
  const sessionsLoading = useAgentStore((s) => s.code.loading);
  const sessionsError = useAgentStore((s) => s.code.error);
  const refreshSessions = useAgentStore((s) => s.refreshSessions);
  const folders = useAgentStore((s) => s.code.folders);
  const expanded = useAgentStore((s) => s.code.expandedFolders);
  const statuses = useAgentStore((s) => s.code.sessionStatuses);
  const streaming = useAgentStore((s) => s.code.streamingSessionIds);
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const search = useAgentStore((s) => s.code.sidebarSearch);
  const setSearch = useAgentStore((s) => s.setSidebarSearch);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);
  const refreshFolders = useAgentStore((s) => s.refreshFolders);
  const createFolder = useAgentStore((s) => s.createFolder);
  const toggleFolderExpanded = useAgentStore((s) => s.toggleFolderExpanded);
  const moveSessionToFolder = useAgentStore((s) => s.moveSessionToFolder);
  const deleteCodeSession = useAgentStore((s) => s.deleteCodeSession);

  const [agents, setAgents] = useState([]);
  // Auditoria 2026-07-02 (chat-hardening S1): antes o load de agentes engolia
  // erros (.catch vazio) → uma falha de rede/500 ficava IDÊNTICA a "sem agentes"
  // (o empty state "Nenhum agente ainda."). Agora rastreia loading/error pra
  // distinguir os estados e oferecer "tentar de novo".
  const [agentsState, setAgentsState] = useState('loading'); // 'loading' | 'error' | 'ready'
  // undefined = modal fechado; null = "Novo agente"; objeto = editar (abre no schedule).
  const [modalAgent, setModalAgent] = useState(undefined);
  // Modo de seleção múltipla (checkbox) pra excluir VÁRIAS sessões de uma vez —
  // pedido do Pedro ("faça com que eu exclua com um check box"). Fora do modo, a
  // lixeira por-linha (hover) segue funcionando pra exclusão avulsa.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // C3 — schedule pill: mapeia nome do agente → scheduled_task ativa pela
  // convenção "AgentName — auto" (mesma do ChatView legado/AgentModal).
  const { tasks: scheduledTasks, load: reloadScheduledTasks } = useScheduledTasks();
  // Adapters → decidir se um agente roda LOCAL (Ollama/LM Studio) ou CLOUD.
  const { adapters } = useLLMAdapters({ kind: 'text' });
  const adaptersMap = useMemo(
    () => Object.fromEntries((adapters || []).map((ad) => [ad.id, ad])),
    [adapters],
  );
  const agentScheduleMap = useMemo(() => {
    const map = {};
    for (const t of scheduledTasks || []) {
      if (t.status !== 'deleted' && t.name?.includes(' — auto')) {
        map[t.name.replace(' — auto', '').trim()] = t;
      }
    }
    return map;
  }, [scheduledTasks]);
  // Play/stop on-demand por agente: dispara a MESMA rotina do agendamento (o "run das 9h")
  // agora, repetível. Estado = { [agentId]: AbortController } (presença = rodando).
  // ROTEAMENTO (fix 2026-07-10): agente de modelo LOCAL (Ollama/LM Studio) roda pelo
  // caminho LOCAL (runLocalTaskNow → IPC + /local-run) — o cloud run-now responde
  // "agent_loop: unknown model <X>", o run morria failed e nada era entregue no chat.
  // Cloud entra na fila durável; Stop cancela o run reservado no backend.
  // Local propaga o signal ao stream IPC e ao registro da execução.
  const [agentRuns, setAgentRuns] = useState({});
  function emitRunErrorToast(name, err) {
    window.dispatchEvent(new CustomEvent('apolo:toast', {
      detail: { kind: 'error', message: `Falha ao rodar ${name}: ${err || 'erro desconhecido'}` },
    }));
  }
  async function runAgentLocal(a, sched, controller) {
    const result = await runLocalTaskNow(sched, adaptersMap, { signal: controller.signal });
    if (controller.signal.aborted) return; // Stop → descarta o resultado
    reloadScheduledTasks();
    if (result.status !== 'ok') emitRunErrorToast(a.name, result.error);
  }
  async function runAgentCloud(a, sched, controller) {
    const path = `/api/code-ide/schedule/${sched.id}`;
    const res = await apiFetch(`${path}/run`, { method: 'POST' });
    if (controller.signal.aborted && res.data?.runId) {
      const cancelled = await apiFetch(`${path}/runs/${res.data.runId}/cancel`, { method: 'POST' });
      if (!cancelled.ok) emitRunErrorToast(a.name, cancelled.error || 'Não foi possível confirmar o cancelamento');
      reloadScheduledTasks();
      return;
    }
    if (res.data?.status === 'queued') window.dispatchEvent(new CustomEvent('apolo:toast', { detail: { kind: 'info', message: 'Tarefa na fila. Acompanhe ou cancele em Agenda → Histórico e resultados.' } }));
    if (res.ok) reloadScheduledTasks(); // a pill de "última execução" reflete o run
    else if (!controller.signal.aborted) emitRunErrorToast(a.name, res.error);
  }
  async function toggleAgentRun(a, sched) {
    const inflight = agentRuns[a.id];
    if (inflight) { inflight.abort(); return; } // clique enquanto roda = Parar
    if (!sched?.id) return;
    const controller = new AbortController();
    setAgentRuns((m) => ({ ...m, [a.id]: controller }));
    try {
      if (isTaskLocal(sched, adaptersMap)) await runAgentLocal(a, sched, controller);
      else await runAgentCloud(a, sched, controller);
    } catch { /* abort (Stop) ou rede — silencioso */ } finally {
      setAgentRuns((m) => { const n = { ...m }; delete n[a.id]; return n; });
    }
  }
  // Auditoria 2026-07-02 (chat-hardening S2 #3): guard anti-double-click — sem ele,
  // 2 cliques rápidos num agente disparavam 2 createCodeSession = 2 sessões.
  const [startingAgent, setStartingAgent] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // folderId | 'recents'
  // Cérebro unificado (recuperado do Aba-Apolo, adaptado ao constraint do Pedro:
  // memória COMPARTILHADA entre Code e Chat). Reusa MemoryEditor → mesma view
  // /api/code-ide/memory (composer 'code', lê NULL+'code'), então uma memória
  // criada aqui aparece na aba Code e vice-versa. Sem namespace 'chat' (isso
  // DIVIDIRIA a UI — o oposto do pedido). Acessível mesmo sem sessão aberta.
  const [memoryOpen, setMemoryOpen] = useState(false);

  // Linha de nav (Home/Agenda/Kanban/Integrações/Painel) — paridade com a aba
  // Code (pedido do Pedro 2026-08-14: "os mesmos ícones da aba code"). Home/Agenda/
  // Kanban trocam a `chatView` no ChatWorkspace via evento `apolo:setChatView`
  // (nome PRÓPRIO — não `apolo:setCodeView` — pra não vazar entre abas, que ficam
  // ambas montadas com display:none no keep-alive do AppShell). Integrações e
  // Painel são modais auto-contidos reusados do Code (mesmo conteúdo, D1-backed).
  const [chatView, setChatView] = useState('home');
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [commandCenterOpen, setCommandCenterOpen] = useState(false);
  useEffect(() => {
    const onSetChatView = (e) => {
      const v = e?.detail?.view;
      if (v === 'home' || v === 'schedules' || v === 'sprints') setChatView(v);
    };
    window.addEventListener('apolo:setChatView', onSetChatView);
    return () => window.removeEventListener('apolo:setChatView', onSetChatView);
  }, []);

  const loadAgents = useCallback(() => {
    setAgentsState('loading');
    apiFetch('/api/agents')
      .then((r) => {
        if (r.ok) {
          setAgents(r.data.agents ?? []);
          setAgentsState('ready');
        } else {
          setAgentsState('error');
        }
      })
      .catch(() => setAgentsState('error'));
  }, []);

  useEffect(() => {
    refreshFolders();
  }, [refreshFolders]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Sessões de chat = kind 'chat' (0097). Fallback local+direct pras legadas.
  const filtered = filterSessionsForSurface(sessions, { surface: 'chat', search });
  const chatSessions = filterSessionsForSurface(sessions, { surface: 'chat' });
  const loose = filtered.filter((s) => !s.folder_id);
  const inFolder = (fid) => filtered.filter((s) => s.folder_id === fid);

  function statusOf(s) {
    if (streaming?.[s.id]) return 'running';
    return statuses?.[s.id]?.status || 'idle';
  }

  async function handleNewFolder() {
    const name = typeof window !== 'undefined' ? window.prompt('Nome da pasta') : null;
    if (name && name.trim()) await createFolder(name.trim());
  }

  function onDropInto(folderId) {
    // Auditoria 2026-07-02 (chat-hardening S2 #6): drop na MESMA pasta era um POST
    // redundante (e podia clobbar folder_id num refreshSessions concorrente).
    if (dragId) {
      const dragged = sessions.find((s) => s.id === dragId);
      if (!dragged || dragged.folder_id !== folderId) moveSessionToFolder(dragId, folderId);
    }
    setDragId(null);
    setDropTarget(null);
  }

  // Auditoria 2026-07-02 (chat-hardening S2 #1): deletar uma sessão de chat com
  // run VIVA só chamava deleteCodeSession — a run seguia órfã (o loop editava o
  // FS sem Stop) e reaparecia mislabelada na BackgroundRunsBar. evictRun aborta o
  // controller + limpa o cache (mesmo padrão #429 do CodeSidebar). Só evicta se o
  // delete não foi cancelado (ex.: worktree sujo — inaplicável a chat, mas seguro).
  async function deleteSessionNow(id) {
    const res = await deleteCodeSession(id);
    if (res?.ok !== false) evictRun(id);
  }
  async function handleDeleteSession(id) {
    if (typeof window !== 'undefined' && !window.confirm('Excluir esta sessão?')) return;
    await deleteSessionNow(id);
  }
  // Seleção múltipla (checkbox) — toggle, sair, e excluir em lote (1 confirmação).
  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function exitSelect() {
    setSelectMode(false);
    setSelectedIds(new Set());
  }
  async function bulkDeleteSelected() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const plural = ids.length > 1 ? 's' : '';
    if (typeof window !== 'undefined' && !window.confirm(`Excluir ${ids.length} sessõe${plural}?`)) return;
    await Promise.all(ids.map((id) => deleteSessionNow(id)));
    exitSelect();
  }

  // Clicar num agente abre uma sessão de chat COM ele (agent_id + o modelo do
  // agente). Antes os agentes eram read-only — "não funcionava" ao clicar.
  // BUG 2026-07-04 — cada clique criava OUTRA sessão vazia: o usuário clicava no
  // agente pra REABRIR a conversa, ganhava uma em branco e concluía que "as
  // mensagens não ficam gravadas" (dúzias de News AI/Market Stock com 0 msgs no
  // D1). Agora o clique REUSA a conversa de chat mais recente daquele agente;
  // só cria quando não existe nenhuma. Nova conversa = botão "Nova sessão".
  async function startAgentSession(a) {
    if (startingAgent) return; // guard: ignora cliques enquanto uma criação está em voo
    setStartingAgent(true);
    try {
      const store = useAgentStore.getState();
      const existing = (store.code.sessions || [])
        .filter((s) => s.agent_id === a.id && (s.kind === 'chat' || s.flags?.kind === 'chat'))
        .sort((x, y) => String(y.last_message_at || y.updated_at || '').localeCompare(String(x.last_message_at || x.updated_at || '')))[0];
      if (existing) { store.setActiveSession(existing.id); return; }
      const res = await store.createCodeSession({
        name: a.name || 'Agente',
        agent_id: a.id,
        model: a.model || undefined,
        scope: 'local',
        kind: 'chat', // 0097 — superfície de 1ª classe (separa da aba Code)
        mode: 'direct',
      });
      if (!res.ok) {
        window.dispatchEvent(
          new CustomEvent('apolo:toast', {
            detail: { kind: 'error', message: `Falha ao abrir o agente: ${res.error || 'erro desconhecido'}` },
          }),
        );
      }
    } finally {
      setStartingAgent(false);
    }
  }

  function SessionRow({ s, nested }) {
    const active = s.id === activeSessionId;
    const selected = selectedIds.has(s.id);
    return (
      <li
        draggable={!selectMode}
        onDragStart={() => setDragId(s.id)}
        onDragEnd={() => {
          setDragId(null);
          setDropTarget(null);
        }}
        onClick={() => (selectMode ? toggleSelect(s.id) : setActiveSession(s.id))}
        className={`group flex cursor-pointer items-center gap-2 rounded-lg py-1.5 pr-2 transition ${
          nested ? 'pl-7' : 'pl-2'
        } ${
          selectMode && selected
            ? 'bg-apolo-accentMuted text-apolo-textPrimary'
            : active
              ? 'bg-apolo-surface4 text-apolo-textPrimary'
              : 'text-apolo-textSecondary hover:bg-apolo-surface2'
        }`}
      >
        {selectMode ? (
          selected ? (
            <CheckSquare size={14} aria-hidden="true" className="shrink-0 text-apolo-accent" />
          ) : (
            <Square size={14} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
          )
        ) : (
          <MessageSquare size={13} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
        )}
        <span className="flex-1 truncate text-xs">{s.name || 'Sessão'}</span>
        {!selectMode && <Dot status={statusOf(s)} />}
        {!selectMode && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteSession(s.id);
            }}
            aria-label="Excluir sessão"
            className="hidden text-apolo-textTertiary transition hover:text-apolo-error group-hover:inline-flex"
          >
            <Trash2 size={12} aria-hidden="true" />
          </button>
        )}
      </li>
    );
  }

  return (
    <aside className="flex h-full w-[240px] shrink-0 flex-col gap-3 border-r border-apolo-border bg-apolo-surface1 px-2.5 py-3">
      {/* Busca + nova sessão */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 rounded-lg border border-apolo-border bg-apolo-bg px-2.5 py-1.5">
          <Search size={14} aria-hidden="true" className="text-apolo-textTertiary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar"
            aria-label="Buscar sessões"
            className="w-full bg-transparent text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={onNewSession}
          className="inline-flex items-center gap-2 rounded-lg border border-apolo-borderStrong px-2.5 py-1.5 text-xs font-medium text-apolo-textPrimary transition hover:bg-apolo-surface2"
        >
          <Plus size={14} aria-hidden="true" className="text-apolo-accent" />
          Novo chat
        </button>
      </div>

      {/* Nav — MESMOS ícones da aba Code (Home/Agenda/Kanban/Integrações/Painel).
          Home/Agenda/Kanban trocam a view do ChatWorkspace; Integrações/Painel
          abrem modais auto-contidos (mesmo conteúdo compartilhado do Code). */}
      <nav
        aria-label="Atalhos"
        className="flex flex-wrap items-center justify-start gap-2 px-1 py-1"
      >
        <SidebarNavItem
          accent="var(--apolo-accent)"
          index={0}
          onClick={() => {
            setActiveSession(null);
            window.dispatchEvent(new CustomEvent('apolo:setChatView', { detail: { view: 'home' } }));
            window.dispatchEvent(new CustomEvent('apolo:chatNewSession'));
          }}
          icon={Home}
          label="Home"
          active={activeSessionId == null && chatView === 'home'}
          title="Home — volta pra tela inicial"
        />
        <SidebarNavItem
          accent="var(--apolo-success)"
          index={1}
          onClick={() =>
            window.dispatchEvent(new CustomEvent('apolo:setChatView', { detail: { view: 'schedules' } }))
          }
          icon={CalendarClock}
          label="Agenda"
          active={activeSessionId == null && chatView === 'schedules'}
          title="Agenda — o que está programado"
        />
        <SidebarNavItem
          accent="var(--apolo-highlight)"
          index={2}
          onClick={() =>
            window.dispatchEvent(new CustomEvent('apolo:setChatView', { detail: { view: 'sprints' } }))
          }
          icon={Columns3}
          label="Kanban"
          active={activeSessionId == null && chatView === 'sprints'}
          title="Kanban — gerenciamento de demandas"
        />
        <SidebarNavItem
          accent="var(--apolo-accent-cyan)"
          index={3}
          onClick={() => setIntegrationsOpen(true)}
          icon={PlugZap}
          label="Integrações"
          title="Integrações — Linear, Notion, Slack, Jira"
        />
        <SidebarNavItem
          accent="var(--apolo-accent-pink)"
          index={4}
          onClick={() => setCommandCenterOpen(true)}
          icon={LayoutDashboard}
          label="Painel"
          title="Command Center — visão de todas as sessões"
        />
      </nav>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        {/* AGENTES */}
        <section className="flex flex-col gap-1">
          <div className="cx-eyebrow px-1 pb-1">Agentes</div>
          {agentsState === 'loading' && agents.length === 0 ? (
            <p className="px-1 text-[11px] text-apolo-textTertiary">Carregando agentes…</p>
          ) : agentsState === 'error' ? (
            <p className="px-1 text-[11px] text-apolo-textTertiary">
              Não foi possível carregar os agentes.{' '}
              <button
                type="button"
                onClick={loadAgents}
                className="text-apolo-accent underline transition hover:text-apolo-textPrimary"
              >
                Tentar de novo
              </button>
            </p>
          ) : agents.length === 0 ? (
            <p className="px-1 text-[11px] text-apolo-textTertiary">Nenhum agente ainda.</p>
          ) : (
            <ul className="flex flex-col">
              {agents.map((a) => {
                const sched = agentScheduleMap[a.name];
                const agentRunning = !!agentRuns[a.id];
                return (
                  <li key={a.id} className="group flex items-center">
                    <button
                      type="button"
                      onClick={() => startAgentSession(a)}
                      disabled={startingAgent}
                      title={`Conversar com ${a.name}`}
                      className="flex min-w-0 flex-1 flex-col rounded-lg px-2 py-1.5 text-left text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2">
                        <HugeiconsIcon
                          icon={iconForAgentEmoji(a.emoji)}
                          size={15}
                          aria-hidden="true"
                          className="shrink-0"
                        />
                        <span className="flex-1 truncate text-xs">{a.name}</span>
                      </div>
                      {sched ? (
                        <div className="mt-0.5 pl-6">
                          <SchedulePill task={sched} />
                        </div>
                      ) : null}
                    </button>
                    {sched ? (
                      <button
                        type="button"
                        onClick={() => toggleAgentRun(a, sched)}
                        aria-label={agentRunning ? `Parar ${a.name}` : `Rodar ${a.name} agora`}
                        title={agentRunning ? 'Parar' : 'Rodar agora (mesma rotina do agendamento)'}
                        className={`shrink-0 rounded-md p-1 transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary ${
                          agentRunning ? 'text-apolo-textPrimary' : 'text-apolo-textTertiary'
                        }`}
                      >
                        {agentRunning
                          ? <Square size={12} fill="currentColor" className="animate-pulse" aria-hidden="true" />
                          : <Play size={13} aria-hidden="true" />}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setModalAgent(a)}
                      aria-label={`Editar agendamento de ${a.name}`}
                      title={sched ? 'Editar agendamento' : 'Agendar'}
                      className={`shrink-0 rounded-md p-1 text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary ${
                        sched ? '' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
                      }`}
                    >
                      <CalendarClock size={13} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => setModalAgent(null)}
            className="mt-0.5 inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          >
            <Plus size={13} aria-hidden="true" className="text-apolo-accent" />
            Novo agente
          </button>
        </section>

        {/* Divisor entre AGENTES e SESSÕES */}
        <div className="h-px shrink-0 bg-apolo-border" aria-hidden="true" />

        {/* SESSÕES */}
        <section className="flex flex-col gap-1">
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="cx-eyebrow">Chats</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
                aria-pressed={selectMode}
                aria-label={selectMode ? 'Sair da seleção' : 'Selecionar sessões para excluir'}
                title={selectMode ? 'Sair da seleção' : 'Selecionar para excluir'}
                className={`transition ${selectMode ? 'text-apolo-accent' : 'text-apolo-textTertiary hover:text-apolo-textPrimary'}`}
              >
                <CheckSquare size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleNewFolder}
                aria-label="Nova pasta"
                title="Nova pasta"
                className="text-apolo-textTertiary transition hover:text-apolo-textPrimary"
              >
                <FolderPlus size={14} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Barra de exclusão em lote (modo seleção via checkbox) */}
          {selectMode && (
            <div className="mb-1 flex items-center gap-1.5 px-1">
              <button
                type="button"
                onClick={bulkDeleteSelected}
                disabled={selectedIds.size === 0}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-apolo-error/40 bg-apolo-error/10 px-2 py-1 text-[11px] font-medium text-apolo-error transition hover:bg-apolo-error/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 size={12} aria-hidden="true" />
                Excluir{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </button>
              <button
                type="button"
                onClick={exitSelect}
                className="rounded-lg px-2 py-1 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
              >
                Cancelar
              </button>
            </div>
          )}

          {/* Pastas */}
          {folders.map((f) => {
            const open = !!expanded[f.id];
            const items = inFolder(f.id);
            const isDrop = dropTarget === f.id;
            return (
              <div key={f.id}>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dropTarget !== f.id) setDropTarget(f.id);
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(null);
                  }}
                  onDrop={() => onDropInto(f.id)}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 transition ${
                    isDrop ? 'bg-apolo-accentMuted' : 'hover:bg-apolo-surface2'
                  }`}
                  onClick={() => toggleFolderExpanded(f.id)}
                >
                  <ChevronRight
                    size={13}
                    aria-hidden="true"
                    className={`shrink-0 text-apolo-textTertiary transition-transform ${open ? 'rotate-90' : ''}`}
                  />
                  <span className="flex-1 truncate text-xs text-apolo-textSecondary">{f.name}</span>
                  <span className="text-[10px] text-apolo-textTertiary">{items.length}</span>
                </div>
                {open && (
                  <ul className="flex flex-col">
                    {items.map((s) => (
                      <SessionRow key={s.id} s={s} nested />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {/* Recents (soltas) — drop aqui desagrupa */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (dropTarget !== 'recents') setDropTarget('recents');
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(null);
            }}
            onDrop={() => onDropInto(null)}
            className={`mt-1 rounded-lg ${dropTarget === 'recents' ? 'bg-apolo-accentMuted' : ''}`}
          >
            <ul className="flex flex-col">
              {loose.map((s) => (
                <SessionRow key={s.id} s={s} />
              ))}
            </ul>
            {/* Auditoria 2026-07-02 (chat-hardening S1): distingue erro/loading do
                empty state — antes uma falha de refreshSessions ficava idêntica a
                "sem sessões". */}
            {chatSessions.length === 0 && sessionsError ? (
              <p className="px-2 py-1 text-[11px] text-apolo-textTertiary">
                Não foi possível carregar as sessões.{' '}
                <button
                  type="button"
                  onClick={() => refreshSessions()}
                  className="text-apolo-accent underline transition hover:text-apolo-textPrimary"
                >
                  Tentar de novo
                </button>
              </p>
            ) : chatSessions.length === 0 && sessionsLoading ? (
              <p className="px-2 py-1 text-[11px] text-apolo-textTertiary">Carregando sessões…</p>
            ) : loose.length === 0 && folders.length === 0 ? (
              <p className="px-2 py-1 text-[11px] text-apolo-textTertiary">
                Nenhuma sessão. Comece pelo composer.
              </p>
            ) : null}
          </div>
        </section>
      </div>

      {/* Cérebro unificado — footer persistente (acessível sem sessão aberta). */}
      <button
        type="button"
        onClick={() => setMemoryOpen(true)}
        className="inline-flex items-center gap-2 rounded-lg border border-apolo-border px-2.5 py-1.5 text-xs font-medium text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
      >
        <BookOpen size={14} aria-hidden="true" className="text-apolo-textTertiary" />
        Memórias
      </button>

      {memoryOpen && (
        <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Memórias compartilhadas">
          <button
            type="button"
            aria-label="Fechar memórias"
            className="flex-1 bg-black/40"
            onClick={() => setMemoryOpen(false)}
          />
          <div className="flex h-full w-[340px] max-w-[85vw] flex-col border-l border-apolo-border bg-apolo-surface1 shadow-xl">
            <div className="flex items-center justify-between border-b border-apolo-border px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-apolo-textPrimary">
                <BookOpen size={13} aria-hidden="true" className="text-apolo-accent" />
                Memórias · compartilhadas com o Code
              </span>
              <button
                type="button"
                onClick={() => setMemoryOpen(false)}
                aria-label="Fechar"
                className="text-apolo-textTertiary transition hover:text-apolo-textPrimary"
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <MemoryEditor />
            </div>
          </div>
        </div>
      )}

      {/* Integrações & Painel — modais compartilhados com a aba Code (mesmo
          conteúdo D1-backed). Abertos pela linha de nav acima. */}
      <IntegrationsView open={integrationsOpen} onClose={() => setIntegrationsOpen(false)} />
      <CommandCenterModal open={commandCenterOpen} onClose={() => setCommandCenterOpen(false)} />

      <AgentModal
        open={modalAgent !== undefined}
        agent={modalAgent}
        onClose={() => setModalAgent(undefined)}
        onSaved={() => {
          setModalAgent(undefined);
          loadAgents();
          reloadScheduledTasks(); // a pill reflete o schedule recém-salvo
        }}
      />
    </aside>
  );
}
